import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import { parse as parseParams } from 'hub-http/helpers/params';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import { ReviewRatingInput, ReviewSelectInput, ReviewTextInput } from 'trellis-ecosystem';
import { EcosystemReviewWizard } from 'marketplace-ui-reviews/components/wizard/EcosystemReviewWizard';
import { PENDING } from 'marketplace-ui-reviews/constants/UnreviewableReason';
// @ts-expect-error untyped dependency
import { getIsPublicApp } from '../../selectors/app';
import { getServices } from '../../slices/serviceCatalogSlice';
import { ProfileReviewWizardHeader } from './ProfileReviewWizardHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VALIDATION_SCHEMA = {
  ratings: {
    OVERALL: {
      required: true
    }
  },
  content: {
    TITLE: {
      required: true
    },
    REVIEW: {
      required: true,
      minCharacters: 75,
      maxCharacters: 3000
    }
  },
  tags: {
    CATALOG_SERVICES: {
      minSelected: 1,
      maxSelected: 10
    }
  }
};
const emptyReview = {
  content: {},
  ratings: {
    OVERALL: 0
  },
  tags: {
    CATALOG_SERVICES: []
  }
};
const EDIT_REASONS = ['MISSPELLING', 'NEW_INFORMATION', 'ISSUE_RESOLVED', 'APPROACHED_BY_PARTNER', 'OTHER'];
export function ProfileReviewWizard({
  editedReview,
  onClose,
  onConfirm,
  onTrackInteraction,
  profileListing,
  unreviewableReason
}) {
  const {
    search
  } = useLocation();
  const parsedSearch = parseParams(search.slice(1));
  const reviewSource = parsedSearch['eco_review_source'] || 'details';
  const isPublic = useSelector(getIsPublicApp);
  const catalogServices = useSelector(getServices);
  const isEditing = !!editedReview;
  const {
    id: profileId,
    name,
    logoUrl,
    slug
  } = profileListing;
  let defaultReview;
  if (isEditing && editedReview) {
    const {
      answers,
      ratings,
      tags
    } = editedReview.toJSON();
    defaultReview = {
      content: answers,
      ratings,
      tags
    };
  } else {
    try {
      const storedReview = JSON.parse(sessionStorage.getItem(`${slug}_review`));
      defaultReview = storedReview || emptyReview;
    } catch (__error) {
      defaultReview = emptyReview;
    }
  }
  const [originalReview] = useState(isEditing ? {
    content: editedReview.toJSON().answers,
    ratings: editedReview.toJSON().ratings,
    tags: editedReview.toJSON().tags
  } : undefined);
  const [editReason, setEditReason] = useState('');
  const [overallRating, setOverallRating] = useState();
  useEffect(() => {
    onTrackInteraction(isEditing ? 'open edit review wizard' : 'open review wizard', {
      reviewSource,
      section: 'review'
    });
  }, [isEditing, onTrackInteraction, reviewSource]);
  useEffect(() => {
    if (!isEditing && unreviewableReason && unreviewableReason !== `ReviewError.${PENDING}`) {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.error.${unreviewableReason}`
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.profile.reviews.unreviewable.titleText"
        }),
        type: 'danger'
      });
    }
  }, [isEditing, unreviewableReason]);
  const handleSuccess = useCallback(() => {
    onTrackInteraction('submit review', {
      reviewSource,
      section: 'review'
    });
    sessionStorage.removeItem(`${slug}_review`);
    onConfirm();
  }, [onConfirm, onTrackInteraction, reviewSource, slug]);
  const handleClose = useCallback(() => {
    onTrackInteraction('dismiss review wizard', {
      reviewSource,
      section: 'review'
    });
    onClose();
  }, [onClose, onTrackInteraction, reviewSource]);
  const onChange = useCallback(review => {
    if (review.ratings['OVERALL'] !== overallRating) {
      onTrackInteraction('set overall rating', {
        section: 'review',
        value: review.ratings['OVERALL']
      });
      setOverallRating(review.ratings['OVERALL']);
    }
    if (!isEditing) {
      sessionStorage.setItem(`${slug}_review`, JSON.stringify(review));
    }
  }, [isEditing, onTrackInteraction, overallRating, slug]);
  const WizardHeader = useMemo(() => () => /*#__PURE__*/_jsx(ProfileReviewWizardHeader, {
    name: name,
    logoUrl: logoUrl
  }), [logoUrl, name]);
  if (isPublic || !isEditing && unreviewableReason) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "REVIEW_WIZARD_LOAD"
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "REVIEW_WIZARD_LOAD",
    children: /*#__PURE__*/_jsxs(EcosystemReviewWizard, {
      defaultReview: defaultReview,
      HeaderComponent: WizardHeader,
      isEditing: isEditing,
      editReason: editReason,
      offeringId: profileId,
      onChange: onChange,
      onClose: handleClose,
      onSuccess: handleSuccess,
      originalReview: originalReview,
      reviewId: editedReview && editedReview.get('id'),
      reviewType: "PROFILE",
      validationSchema: VALIDATION_SCHEMA,
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        marginBlock: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.profile.reviews.write.wizard.header",
          options: {
            name
          }
        })
      }), /*#__PURE__*/_jsx(ReviewRatingInput, {
        fieldKey: "OVERALL",
        label: unescapedText('ecosystem-marketplace-solutions-ui.profile.reviews.write.wizard.labels.OVERALL')
      }), isEditing && /*#__PURE__*/_jsx(FormControl, {
        label: unescapedText('ecosystem-marketplace-solutions-ui.profile.reviews.write.wizard.labels.EDIT_REASON'),
        required: true,
        children: /*#__PURE__*/_jsx(Select, {
          onChange: ({
            target: {
              value
            }
          }) => {
            setEditReason(value);
          },
          options: EDIT_REASONS.map(reason => ({
            value: reason,
            text: unescapedText(`ecosystem-marketplace-solutions-ui.profile.reviews.write.wizard.editReasons.${reason}`)
          }))
        })
      }), /*#__PURE__*/_jsx(ReviewSelectInput, {
        fieldKey: "CATALOG_SERVICES",
        label: unescapedText('ecosystem-marketplace-solutions-ui.profile.reviews.write.wizard.labels.CATALOG_SERVICES'),
        multi: true,
        options: catalogServices.map(service => ({
          text: service.name,
          value: service.id
        }))
      }), /*#__PURE__*/_jsx(ReviewTextInput, {
        fieldKey: "TITLE",
        label: unescapedText('ecosystem-marketplace-solutions-ui.profile.reviews.write.wizard.labels.TITLE'),
        placeholder: unescapedText('ecosystem-marketplace-solutions-ui.profile.reviews.write.wizard.placeholders.TITLE'),
        resize: false,
        rows: 1
      }), /*#__PURE__*/_jsx(ReviewTextInput, {
        fieldKey: 'REVIEW',
        label: unescapedText(`ecosystem-marketplace-solutions-ui.profile.reviews.write.wizard.labels.REVIEW`),
        placeholder: unescapedText(`ecosystem-marketplace-solutions-ui.profile.reviews.write.wizard.placeholders.REVIEW`, {
          name
        }),
        rows: 4
      })]
    })
  });
}